import { useMemo, useRef, useState } from "react";
import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  aggridHeaderClass,
  pdaCellStyle,
} from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { PDA1020_R01Dto } from "./PDA1020_R01Dto";
import usePDA1020_R01Store from "./Store_PDA1020_R01";

type PDA1020_R01GridRow = PDA1020_R01Dto & { inserted?: boolean };

const PDA1020_R01Table = () => {
  const { PDA1020_R01List } = usePDA1020_R01Store();
  const gridRef = useRef<AgGridReact<PDA1020_R01GridRow>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  const rowData = useMemo(
    () => [...PDA1020_R01List] as PDA1020_R01GridRow[],
    [PDA1020_R01List]
  );

  const totalProductQty = useMemo(
    () =>
      PDA1020_R01List.reduce(
        (total, row) => total + Number(row.productQty ?? 0),
        0
      ),
    [PDA1020_R01List]
  );

  const pinnedBottomRowData = useMemo(
    () =>
      [
        {
          itemNm: "",
          itemSpec: "",
          productQty: totalProductQty,
        } as PDA1020_R01GridRow,
      ],
    [totalProductQty]
  );

  const columnDefs = useMemo<ColDef<PDA1020_R01GridRow>[]>(
    () => [
      ...getDefaultRowIndexColumnConfig(),
      {
        headerName: "품명",
        field: "itemNm",
        valueGetter: (params) =>
          params.node?.rowPinned === "bottom" ? "" : params.data?.itemNm || "-",
        tooltipValueGetter: (params) => params.data?.itemNm || "-",
        cellStyle: {
          ...pdaCellStyle,
          textAlign: "left",
          justifyContent: "flex-start",
        },
        headerClass: aggridHeaderClass,
        flex: 1.2,
        minWidth: 90,
      },
      {
        headerName: "규격",
        field: "itemSpec",
        valueGetter: (params) =>
          params.node?.rowPinned === "bottom" ? "" : params.data?.itemSpec || "-",
        tooltipValueGetter: (params) => params.data?.itemSpec || "-",
        cellStyle: {
          ...pdaCellStyle,
          textAlign: "left",
          justifyContent: "flex-start",
        },
        headerClass: aggridHeaderClass,
        flex: 1.05,
        minWidth: 88,
      },
      {
        headerName: "생산량",
        field: "productQty",
        valueFormatter: (params) =>
          Number(params.value ?? 0).toLocaleString(),
        cellStyle: (params) => ({
          ...pdaCellStyle,
          textAlign: "right",
          fontWeight: params.node?.rowPinned === "bottom" ? "700" : "400",
          justifyContent: "flex-end",
        }),
        headerClass: aggridHeaderClass,
        flex: 0.75,
        minWidth: 72,
      },
    ],
    []
  );

  const initSelected = () => {};

  return (
    <div className="flex-1 p-2 flex w-full h-full flex-col overflow-hidden">
      <GridContextWrapper
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={rowData}
        initSelected={initSelected}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1020_R01_ColumnState"
        enableDataHistory={false}
      >
        <Grid_Select<PDA1020_R01GridRow>
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          setHoveredRowIndex={setHoveredRowIndex}
          columnStateKey="PDA1020_R01_ColumnState"
          setColumnState={setColumnState}
          pinnedBottomRowData={pinnedBottomRowData}
        />
      </GridContextWrapper>
    </div>
  );
};

export default PDA1020_R01Table;
